import type { ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import styles from './ModalHeader.module.css';

/**
 * Figma: `_Modal header` (2013:10605 → `Type=Left aligned`, 2013:10642). The
 * `_` marks it internal; `Modal` and `Drawer` both instance it, so it is shared
 * here rather than drawn twice.
 *
 * `Type` has a single option and does not become a prop. The two toggles do:
 *
 * - `Featured icon` (toggle) + the `placeholder` instance → `icon`. A slot, so
 *   absent means not rendered — Figma defaults it on with the placeholder
 *   glyph, which is a stand-in rather than a design.
 * - `X close button` (toggle) → `onClose`. The button is only rendered when
 *   there is something for it to do.
 */
export type ModalHeaderProps = {
  title: ReactNode;
  /** Figma `Supporting text`. */
  description?: ReactNode;
  /** Figma `Featured icon` — a 12px glyph beside the title. */
  icon?: ReactNode;
  /** Figma `X close button`. Omit to render without one. */
  onClose?: () => void;
  /** Accessible name of the close button. Not in Figma. */
  closeLabel?: string;
  /** Ids the dialog points `aria-labelledby` / `aria-describedby` at. */
  titleId?: string;
  descriptionId?: string;
};

export function ModalHeader({
  title,
  description,
  icon,
  onClose,
  closeLabel = 'Close',
  titleId,
  descriptionId,
}: ModalHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.content}>
        <div className={styles.text}>
          <div className={styles.titleRow}>
            {icon !== undefined && <span className={styles.icon}>{icon}</span>}
            <Text as="h2" id={titleId} size="md" weight="regular" color="primary" className={styles.title}>
              {title}
            </Text>
          </div>
          {description !== undefined && (
            <Text
              as="p"
              id={descriptionId}
              size="sm"
              leading="normal"
              weight="regular"
              color="secondary"
              className={styles.description}
            >
              {description}
            </Text>
          )}
        </div>
        {onClose !== undefined && (
          <Button
            hierarchy="ghostGrey"
            iconLeading={<Icon name="close" />}
            aria-label={closeLabel}
            onClick={onClose}
          />
        )}
      </div>
    </div>
  );
}
